import React, { useState } from "react";
import apiPath from "../../utils/apiPath";
import { apiPost } from "../../utils/apiFetch";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useForm, useFieldArray } from "react-hook-form";
import useToastContext from "hooks/useToastContext";
import OInputField from "components/reusable/OInputField";
import ErrorMessage from "components/ErrorMessage";
import { AiFillDelete } from "react-icons/ai";

const EmployeeDetail = ({ employeeItem, handleCategory, allSubscription }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      allowance: [
        {
          allowanceChild: "",
        },
      ],
      deduction: [
        {
          deductionChild: "",
        },
      ],
    },
    mode: "onChange",
    shouldFocusError: true,
  });

  const preparePayload = (employeeItems, data) => {
    return {
      employeeID: employeeItems?.Employee?._id,
      allowance: data?.allowance,
      deduction: data?.deduction,
    };
  };

  const handleSuccess = (result) => {
    if (result.data.success) {
      allSubscription();
      handleCategory(false);
      notification.success(result?.data.message);
    } else {
      notification.error(result?.data.message);
    }
  };

  const handleError = (error) => {
    console.log("error in get all users list==>>>>", error);
    notification.error(error.message);
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      const payload = preparePayload(employeeItem, data);
      const path = apiPath.createSalary;
      const result = await apiPost(path, payload);

      handleSuccess(result);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const preventMax = (e) => {
    if (e.target.value.length > 10) {
      e.target.value = e.target.value.slice(0, 10);
    }
  };

  const [netSalary, setSalary] = useState(0);
  const [totalDeduction, setTotalDeduction] = useState(0);
  const [totalAllowance, setTotalAllowance] = useState(0);

  const calculationForAllowance = () => {
    const allowanceOne = watch().allowance;
    const deductionOne = watch().deduction;
    const keyToAllowance = "allowanceChild";
    const keyToDeduction = "deductionChild";
    let totalValueAllow = 0;
    let totalValueDeduct = 0;
    allowanceOne.forEach((currentObject) => {
      totalValueAllow += Number(currentObject[keyToAllowance]);
    });
    deductionOne.forEach((currentObject) => {
      totalValueDeduct += Number(currentObject[keyToDeduction]);
    });
    setTotalDeduction(totalValueDeduct);
    setTotalAllowance(totalValueAllow);
    const netValueSalary = totalValueAllow - totalValueDeduct;
    if (totalValueAllow > totalValueDeduct) {
      setSalary(netValueSalary);
    } else {
      setSalary(0);
    }
  };

  const numericInputPattern = (event) => {
    if (!["Backspace", "Delete", "Tab"].includes(event.key) && !/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const { fields: allowanceFields, remove: allowanceRemove, append: allowanceAppend } = useFieldArray({ control, name: "allowance" });

  const { fields: deductionFields, remove: deductionRemove, append: deductionAppend } = useFieldArray({ control, name: "deduction" });

  const renderButtonOrSpinner = () => {
    if (isLoading) {
      return (
        <div className="spinner-container bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150">
          <div className="loading-spinner"></div>
        </div>
      );
    } else {
      return (
        <button
          className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
          type="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {t("O_SAVE")}
        </button>
      );
    }
  };

  return (
    <>
      <div className="p-5 max-h-[750px] overflow-y-auto">
        <div className="py-3">
          <h3 className="text-xl mb-2">{t("PERSONAL_DETAILS")}</h3>
          <ul className="flex flex-wrap mx-[-10px]">
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("EMPLOYEE_ID")}</strong>
              <span className="break-words">{employeeItem?.Employee?.employeeId}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("EMPLOYEE_NAME")}</strong>
              <span className="break-words">{employeeItem?.Employee?.name}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("JOB_TITLE")}</strong>
              <span className="break-words">{employeeItem?.Job?.name}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("DEPARTMENT")}</strong>
              <span className="break-words">{employeeItem?.Department?.name}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("O_EMAIL")}</strong>
              <span className="break-words">{employeeItem?.Employee?.email}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block mb-2 text-slate-500 font-medium">{t("O_DATE_OF_JOINING")}</strong>
              <span className="break-words">{dayjs(employeeItem?.acceptedRejectedAt).format("MM-DD-YYYY")}</span>
            </li>
          </ul>
        </div>
        <div className="py-3">
          <div className="mb-3">
            <div className="block">
              <b>Duration Type</b>
            </div>
            <span className="py-3 border-b border-gray-500  block my-2">{employeeItem?.duration}</span>
          </div>
        </div>
        <div className="py-3 flex">
          <div className="w-1/2 px-2">
            <h3 className="text-xl mb-2">{t("ALLOWANCE")}</h3>
            <div className="">
              {allowanceFields.map((item, index) => {
                const fieldName = `allowance[${index}]`;
                return (
                  <>
                    {" "}
                    <div className="flex  items-center">
                      <OInputField
                        wrapperClassName="relative z-0 w-full group my-2"
                        inputLabel={
                          <>
                            {t("ALLOWANCE")} ($)
                            <span className="text-red-500">*</span>
                          </>
                        }
                        type="text"
                        name={`${fieldName}.allowanceChild`}
                        id={`${fieldName}.allowanceChild`}
                        onInput={(e) => preventMax(e)}
                        maxLength={10}
                        onKeyDown={numericInputPattern}
                        register={register(`${fieldName}.allowanceChild`, {
                          required: {
                            value: true,
                            message: "Please enter allowance.",
                          },
                          maxLength: {
                            value: 10,
                            message: "Maximum limit is 10 characters.",
                          },
                          pattern: {
                            value: true,
                            message: "Only digits are allowed.",
                          },
                          validate: (val) => {
                            const { deduction, allowance } = getValues();
                            const totalDeductions = deduction.map((itemsDeduction) => +itemsDeduction?.deductionChild || 0).reduce((a, b) => a + b, 0);
                            const totalAllowances = allowance.map((itemsAllowance) => +itemsAllowance?.allowanceChild || 0).reduce((a, b) => a + b, 0);
                            if (Number(totalDeductions) < Number(totalAllowances) && totalDeductions !== 0) {
                              setError(`deduction[${deduction.length - 1}].deductionChild`, {
                                type: "manual",
                                message: "",
                              });
                            }
                          },
                          onChange: () => {
                            calculationForAllowance();
                          },
                        })}
                      />
                      <div className="ps-2">
                        {index !== 0 ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                allowanceRemove(index);
                                calculationForAllowance();
                              }}
                            >
                              <AiFillDelete className="w-5 h-5 text-red-600" />
                            </button>
                          </>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                    <ErrorMessage message={errors?.allowance?.[index]?.allowanceChild?.message} />
                  </>
                );
              })}
            </div>
            <button
              className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150"
              type="button"
              onClick={() => {
                allowanceAppend({ allowanceChild: "" });
              }}
            >
              {t("O_ADD")}
            </button>
          </div>
          <div className="w-1/2 px-2">
            <h3 className="text-xl mb-2">{t("DEDUCTION")}</h3>
            <div className="">
              {deductionFields.map((item, index) => {
                const fieldName = `deduction[${index}]`;
                return (
                  <>
                    <div className="flex  items-center">
                      <OInputField
                        wrapperClassName="relative z-0 w-full group my-2"
                        inputLabel={
                          <>
                            {t("DEDUCTION")} ($)
                            <span className="text-red-500">*</span>
                          </>
                        }
                        type="text"
                        name={`${fieldName}.deductionChild`}
                        id={`${fieldName}.deductionChild`}
                        onInput={(e) => preventMax(e)}
                        maxLength={10}
                        onKeyDown={numericInputPattern}
                        register={register(`${fieldName}.deductionChild`, {
                          required: {
                            value: true,
                            message: "Please enter deduction.",
                          },
                          maxLength: {
                            value: 10,
                            message: "Maximum limit is 10 characters.",
                          },
                          pattern: {
                            value: true,
                            message: "Only digits are allowed.",
                          },
                          validate: (val) => {
                            const { allowance, deduction } = getValues();
                            const totalDeduction1 = deduction.map((itemDeduction) => +itemDeduction?.deductionChild || 0).reduce((a, b) => a + b, 0);
                            const totalAllowance1 = allowance.map((itemAllowance) => +itemAllowance?.allowanceChild || 0).reduce((a, b) => a + b, 0);
                            if (Number(totalAllowance1) < Number(totalDeduction1)) {
                              return "Deduction must be less than allowance.";
                            }
                          },
                          onChange: () => {
                            calculationForAllowance();
                          },
                        })}
                      />

                      {index !== 0 ? (
                        <>
                          <div className="ps-2">
                            <button
                              type="button"
                              onClick={() => {
                                deductionRemove(index);
                                calculationForAllowance();
                              }}
                            >
                              <AiFillDelete className="w-5 h-5 text-red-600" />
                            </button>
                          </div>
                        </>
                      ) : (
                        ""
                      )}
                    </div>
                    <ErrorMessage message={errors?.deduction?.[index]?.deductionChild?.message} />
                  </>
                );
              })}
            </div>

            <button
              className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1  ease-linear transition-all duration-150"
              type="button"
              onClick={() => {
                deductionAppend({ deductionChild: "" });
              }}
            >
              {t("O_ADD")}
            </button>
          </div>
        </div>
        <div className="py-3">
          <h3>Summary</h3>
          <ul className="flex flex-wrap mx-[-10px]">
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block text-slate-500	font-medium">{t("TOTAL_ALLOWANCE")}</strong>
              <span>${totalAllowance}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block text-slate-500	font-medium">{t("TOTAL_DEDUCTION")}</strong>
              <span>${totalDeduction}</span>
            </li>
            <li className="w-50 md:w-[16.666%] p-3">
              <strong className="block text-slate-500 font-medium	">{t("NET_SALARY")}</strong>
              <span>${netSalary}</span>
            </li>
          </ul>
        </div>
        {renderButtonOrSpinner()}
      </div>
    </>
  );
};

export default EmployeeDetail;
